import React from 'react';
import { motion } from 'framer-motion';
import { Heart, FileDown, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { trackEvent } from '../../lib/analytics';

/**
 * Словарь локализации материалов и вариантов отделки
 */
const RAW_TAGS_MAP = {
  en: {
    wood: 'Solid Wood', mdf: 'MDF Panels', chipboard: 'Chipboard', melamine: 'Melamine', acrylic: 'Acrylic',
    aluminum: 'Aluminum Profile', glass: 'Premium Glass', mirror: 'Mirror', stone: 'Natural Stone',
    natural_veneer: 'Natural Veneer', engineered_veneer: 'Modified Veneer', hpl: 'HPL Panels',
    leather: 'Natural Leather', eco_leather: 'Eco Leather', fabric: 'Fabric / Textiles',
    primer: 'Primer', paint: 'Paint', lacquer: 'Lacquer',
    matte: 'Matte', glossy: 'Glossy', satin: 'Satin', textured: 'Textured', natural: 'Natural', brushed: 'Brushed'
  },
  ru: {
    wood: 'Натуральное Дерево', mdf: 'МДФ Панели', chipboard: 'ДСП / ЛДСП', melamine: 'Меламин', acrylic: 'Акрил',
    aluminum: 'Алюминиевый Профиль', glass: 'Премиальное Стекло', mirror: 'Зеркальное Полотно', stone: 'Натуральный Камень',
    natural_veneer: 'Натуральный Шпон', engineered_veneer: 'Модифицированный Шпон', hpl: 'HPL Панели',
    leather: 'Натуральная Кожа', eco_leather: 'Экокожа', fabric: 'Интерьерный Текстиль',
    primer: 'Архитектурный Грунт', paint: 'Полиуретановая Эмаль', lacquer: 'Защитный Лак',
    matte: 'Матовая', glossy: 'Глянцевая', satin: 'Сатинированная', textured: 'Текстурированная', natural: 'Натуральная фактура', brushed: 'Брашированная'
  },
  nl: {
    wood: 'Massief Hout', mdf: 'MDF Panelen', chipboard: 'Spaanplaat', melamine: 'Melamine', acrylic: 'Acryl',
    aluminum: 'Aluminium', glass: 'Premium Glas', mirror: 'Spiegel', stone: 'Natuursteen',
    natural_veneer: 'Natuurlijk Fineer', engineered_veneer: 'Gemodificeerd Fineer', hpl: 'HPL Panelen',
    leather: 'Natuurlijk Leer', eco_leather: 'Eco-leer', fabric: 'Stof / Textiel',
    primer: 'Primer', paint: 'Polyurethaanlak', lacquer: 'Beschermende Lak',
    matte: 'Mat', glossy: 'Glanzend', satin: 'Satijn', textured: 'Getextureerd', natural: 'Natuurlijk', brushed: 'Geborsteld'
  },
  ro: {
    wood: 'Lemn Masiv', mdf: 'Panouri MDF', chipboard: 'PAL', melamine: 'Melamină', acrylic: 'Acrilic',
    aluminum: 'Aluminiu', glass: 'Sticlă Premium', mirror: 'Oglindă', stone: 'Piatră Naturală',
    natural_veneer: 'Furnir Natural', engineered_veneer: 'Furnir Modificat', hpl: 'Panouri HPL',
    leather: 'Piele Naturală', eco_leather: 'Piele Ecologică', fabric: 'Material / Textile',
    primer: 'Grund', paint: 'Vopsea Poliuretanică', lacquer: 'Lac Protecție',
    matte: 'Mată', glossy: 'Lucioasă', satin: 'Satinată', textured: 'Texturată', natural: 'Naturală', brushed: 'Satinat'
  }
};

/**
 * Безопасное преобразование любого значения в строку
 */
const toStr = (val, lang = 'en') => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (Array.isArray(val)) return val.map((v) => toStr(v, lang)).filter(Boolean).join(', ');
  if (typeof val === 'object') {
    if (val[lang]) return toStr(val[lang], lang);
    if (val.en) return toStr(val.en, lang);
    if (val.ru) return toStr(val.ru, lang);
    if (val.nl) return toStr(val.nl, lang);
    if (val.ro) return toStr(val.ro, lang);
    const first = Object.values(val).find((v) => typeof v === 'string');
    return first ? String(first) : '';
  }
  return String(val);
};

export const ProductInfo = ({
  product = {},
  language = 'en',
  labels = {},
  isInQuote = false,
  isWishlisted = false,
  isB2BApproved = false,
  onAddToQuote,
  onToggleWishlist,
  currentSelection = {}
}) => {
  const currentTokens = RAW_TAGS_MAP[language] || RAW_TAGS_MAP['en'];

  const formatTokenArray = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return '';
    return arr.map((item) => {
      const strKey = toStr(item, language);
      const translated = currentTokens[strKey] || strKey;
      return toStr(translated, language);
    }).filter(Boolean).join(', ');
  };

  const handleAddToQuoteClick = () => {
    if (onAddToQuote) {
      onAddToQuote(currentSelection.materials || null, currentSelection.finishes || null);
    }
  };

  const skuStr = toStr(product?.sku, language);
  const nameStr = toStr(product?.name, language) || 'Product';
  const descStr = toStr(product?.description, language) || toStr(product?.short_description, language);
  
  // Текст материала (100% совпадение с таблицей спецификаций)
  const baseMaterialText =
    (currentSelection?.materials && String(currentSelection.materials).trim())
      ? currentSelection.materials
      : (toStr(product?.material, language) || toStr(product?.selected_custom_material, language) || formatTokenArray(product?.materials) || '—');

  // Текст покрытия
  const baseFinishText =
    (currentSelection?.finishes && String(currentSelection.finishes).trim())
      ? currentSelection.finishes
      : (toStr(product?.finish, language) || toStr(product?.selected_custom_finish, language) || formatTokenArray(product?.finishes) || '—');

  const labelMat = toStr(labels?.material, language) || 'Current Base Material';
  const labelFin = toStr(labels?.finish, language) || 'Current Base Coating';
  const labelInConcept = toStr(labels?.inConcept, language) || 'In Project Specification';
  const labelConsult = toStr(labels?.consultArchitect, language) || 'Consult an Architect';

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-left font-light select-none space-y-8" data-testid="product-info">
      {/* SKU Идентификатор */}
      {skuStr && (
        <p className="text-[10px] uppercase font-mono tracking-[0.25em] text-neutral-400">
          {skuStr}
        </p>
      )}

      {/* Название товара */}
      <h1 className="text-4xl sm:text-5xl font-extralight tracking-tight text-neutral-900 leading-tight" data-testid="product-title">
        {nameStr}
      </h1>

      {/* Описание товара */}
      {descStr && (
        <p className="text-sm font-light text-neutral-600 leading-relaxed max-w-xl">
          {descStr}
        </p>
      )}

      {/* Сетка базовых материалов */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-neutral-200">
        <div>
          <h4 className="text-[10px] uppercase font-mono tracking-[0.2em] text-neutral-400 mb-1.5">
            {labelMat}
          </h4>
          <p className="text-xs font-normal text-neutral-900 leading-relaxed">
            {baseMaterialText}
          </p>
        </div>

        <div>
          <h4 className="text-[10px] uppercase font-mono tracking-[0.2em] text-neutral-400 mb-1.5">
            {labelFin}
          </h4>
          <p className="text-xs font-normal text-neutral-900 leading-relaxed">
            {baseFinishText}
          </p>
        </div>
      </div>

      {/* Кнопки действий */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          type="button"
          onClick={handleAddToQuoteClick}
          className="flex-1 h-12 rounded-none bg-neutral-950 text-white hover:bg-neutral-800 text-xs font-medium uppercase tracking-[0.2em] transition-all"
          data-testid="add-to-quote-btn"
        >
          {isInQuote ? (
            <>
              <Check className="mr-2 w-4 h-4" />
              {labelInConcept}
            </>
          ) : (
            labelConsult
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={onToggleWishlist}
          className={`h-12 w-12 p-0 rounded-none border-neutral-300 hover:border-black transition-all ${
            isWishlisted ? 'bg-neutral-100 text-red-600 border-neutral-950' : 'text-neutral-700'
          }`}
          data-testid="wishlist-btn"
          aria-label="Wishlist"
        >
          <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-red-600 text-red-600' : ''}`} strokeWidth={1.5} />
        </Button>
      </div>

      {/* Файлы для скачивания */}
      {(product?.pdf_url || product?.cad_url || product?.bim_url || product?.tech_sheet_url || product?.model_3d_url) && (
        <div className="border-t border-neutral-200 pt-6">
          <p className="text-[10px] uppercase font-mono tracking-[0.2em] text-neutral-400 mb-3">Downloads & Trade Assets</p>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            {product.pdf_url && (
              <a href={product.pdf_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('catalogue_downloaded', { type: 'pdf_datasheet', sku: skuStr })} className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-black hover:underline" data-testid="download-pdf">
                <FileDown className="w-3.5 h-3.5" />PDF Datasheet
              </a>
            )}
            {!isB2BApproved && product.tech_sheet_url && (
              <a href={product.tech_sheet_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('catalogue_downloaded', { type: 'tech_sheet', sku: skuStr })} className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-black hover:underline" data-testid="download-tech-sheet">
                <FileDown className="w-3.5 h-3.5" />Tech Sheet
              </a>
            )}
            {!isB2BApproved && product.model_3d_url && (
              <a href={product.model_3d_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('catalogue_downloaded', { type: '3d_model', sku: skuStr })} className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-black hover:underline" data-testid="download-3d-model">
                <FileDown className="w-3.5 h-3.5" />3D Model
              </a>
            )}
            {isB2BApproved && product.cad_url && (
              <a href={product.cad_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('catalogue_downloaded', { type: 'cad', sku: skuStr })} className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-black hover:underline" data-testid="download-cad">
                <FileDown className="w-3.5 h-3.5" />CAD Files
              </a>
            )}
            {isB2BApproved && product.bim_url && (
              <a href={product.bim_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('catalogue_downloaded', { type: 'bim', sku: skuStr })} className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-black hover:underline" data-testid="download-bim">
                <FileDown className="w-3.5 h-3.5" />BIM Files
              </a>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ProductInfo;
